import type { SortingFn } from "@tanstack/react-table";

import { FORMAT_CONFIG } from "@/constants/format";
import { roundUpDecimal, truncateDecimal } from "@/helpers/numberHelpers";

type FormatNumberOptions = {
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  isRoundUp?: boolean;
  isTruncate?: boolean;
};

// Phase 2 optimization: Intl.NumberFormat instances are expensive to build, so
// each distinct combination is created once and reused. Keyed by the settings
// themselves rather than by a fixed pair of "standard" formatters, so every
// precision the config asks for is cached, not just one of them.
const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (
  locale: string,
  minimumFractionDigits: number,
  maximumFractionDigits: number
): Intl.NumberFormat => {
  const key = `${locale}|${minimumFractionDigits}|${maximumFractionDigits}`;
  const cached = formatterCache.get(key);
  if (cached) return cached;

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits
  });
  formatterCache.set(key, formatter);
  return formatter;
};

/**
 * Fast number formatting using cached formatters.
 * Use this for high-volume formatting scenarios (e.g., formatting dataset rows).
 *
 * - For integers: formats with no decimal places
 * - For decimals: formats up to `FORMAT_CONFIG.filterKey` places
 * - Returns string without commas for raw numeric comparison
 *
 * @param value - The numeric value to format.
 * @param type - Formatting type: "integer" (default) or "decimal".
 * @returns A formatted string without commas, or String(value) if invalid.
 */
export const formatNumberRaw = (
  value: number,
  type: "integer" | "decimal" = "integer"
): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return String(value);
  }

  const formatter = getFormatter(
    FORMAT_CONFIG.locale,
    0,
    type === "integer" ? 0 : FORMAT_CONFIG.filterKey
  );
  // Remove commas for raw numeric string (used in dataset table filtering)
  return formatter.format(value).replaceAll(",", "");
};

/**
 * Renders a number at an exact digit count, with no grouping separators.
 *
 * Deliberately `toFixed` rather than `Intl`, because the two disagree: `Intl`
 * adds thousands separators, and they round differently on a tie — `12.35`
 * reads `12.3` here and `12.4` through `Intl`. Call sites that already showed a
 * `toFixed` number keep reading the same, and the digit count moves into the
 * config instead of living at the call site.
 *
 * A value that is not a number renders as "NaN", exactly as it did when the
 * call sites held their own `toFixed`.
 *
 * @param value - The numeric value to format.
 * @param digits - Exact decimal places, trailing zeros included.
 * @returns A formatted string.
 */
export const formatFixed = (value: number, digits: number): string =>
  value.toFixed(digits);

/**
 * Formats a number into a localized string.
 *
 * - If `type` is `"integer"`, the value is formatted with no decimals, not rounded.
 * - If `type` is `"decimal"`, the value is rounded **up** to `maximumFractionDigits`.
 *
 * Display code should not call this directly — go through `formatValue` /
 * `formatMeasure` in `@/utils/format`, which read the shared precision config.
 *
 * @param value - The numeric value to format.
 * @param type - Formatting type: "integer" (default) or "decimal".
 * @param options - Locale, fraction digits and rounding mode.
 * @returns A formatted string or original value if invalid.
 */
export const formatNumber = (
  value: number,
  type: "integer" | "decimal" = "integer",
  {
    locale = FORMAT_CONFIG.locale,
    minimumFractionDigits = FORMAT_CONFIG.minFractionDigits,
    maximumFractionDigits = FORMAT_CONFIG.fractionDigits,
    isRoundUp = true,
    isTruncate = false
  }: FormatNumberOptions = {}
): string | number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return value;
  }

  const isInteger = type === "integer";
  let displayValue = value;

  if (!isInteger && isTruncate) {
    displayValue = truncateDecimal(value, maximumFractionDigits);
  } else if (!isInteger && isRoundUp) {
    displayValue = roundUpDecimal(value, maximumFractionDigits);
  }

  return getFormatter(
    locale,
    minimumFractionDigits,
    isInteger ? 0 : maximumFractionDigits
  ).format(displayValue);
};

/**
 * Parses a display string that may contain thousands separators (commas) back
 * into a number, e.g. "1,000" -> 1000, "234,000" -> 234000, "-1,234.5" -> -1234.5.
 *
 * Inputs like `<input>` values formatted with grouping commas would otherwise
 * become `NaN` through a bare `Number()` (and then serialize to `null` in JSON),
 * so callers that need the numeric value of a formatted field must go through
 * this helper. Returns `NaN` for non-numeric input so callers can guard.
 *
 * @param value - The formatted numeric string to parse.
 * @returns The parsed number, or `NaN` if not parseable.
 */
export const parseFormattedNumber = (value: string): number =>
  Number(value.replaceAll(",", ""));

/**
 * Creates a TanStack Table sorting function that compares values numerically.
 *
 * Values that cannot be converted to a number, including `null`, `undefined`,
 * and empty strings, are treated as null and sorted before valid numbers.
 *
 * @template T - The type of data represented by each table row.
 * @returns A TanStack Table sorting function for numeric values.
 * @example
 * {
 *   accessorKey: "share_rate",
 *   header: "Share Rate",
 *   sortingFn: createNumericSortingFn()
 * }
 */
export function createNumericSortingFn<T>(): SortingFn<T> {
  const toNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;

    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  return (rowA, rowB, columnId) => {
    const a = toNumber(rowA.getValue(columnId));
    const b = toNumber(rowB.getValue(columnId));

    if (a === null && b === null) return 0;
    if (a === null) return -1;
    if (b === null) return 1;

    return a - b;
  };
}

/**
 * Formats a number into a compact string representation.
 * @param num - The number to format.
 * @returns A compact formatted string.
 * Example: 1500 -> "1.5K", 2000000 -> "2M"
 * Note: This uses the "compact" notation of Intl.NumberFormat, which may round the number.
 */
export const compactNumber = (num: number, decimal: number = 0): string => {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: decimal
  }).format(num);
};
